<script setup>
import { cloneDeep, difference, isEmpty, isNil, xor } from 'lodash'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import HomepageGroupsApiHandler from '@/api/homepage-groups'
import {
  BUTTON_ICON_DENSITIES,
  BUTTON_SIZES_NEXT,
  BUTTON_VARIANTS
} from '@/utils/components-configurations/app-button'
import { handleError } from '@/utils/error-handling'
import { GLOBAL_GROUP_KEYS } from '@/utils/global-groups'
import { useHomepageSidebarFilteredOutTooltip } from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { NEW_HP_FILTERS_KEYS } from '@/utils/okr-elements/filters'
import { numberOrNullProp } from '@/utils/prop-validators'
import { useLoadingState } from '@/utils/use-loading-state'

import HomepageGroupsFilterLoader from '@/components/home-page/HomepageGroupsFilterLoader'
import HomepageSidebarFilterEmptyState from '@/components/home-page/HomepageSidebarFilterEmptyState'
import HomepageSidebarFilterItem from '@/components/home-page/HomepageSidebarFilterItem'
import HomepageSidebarFilterLayout from '@/components/home-page/HomepageSidebarFilterLayout'
import HomepageSidebarFilterList from '@/components/home-page/HomepageSidebarFilterList'
import HomepageSidebarPinnedList from '@/components/home-page/HomepageSidebarPinnedList'
import HomepageSidebarSegmentTitle from '@/components/home-page/HomepageSidebarSegmentTitle'
import {
  SIDEBAR_GROUPS_LIST_TEST_ID,
  SIDEBAR_LEVEL_UP_BUTTON_TEST_ID,
  SIDEBAR_PARENT_GROUP_TEST_ID,
  SIDEBAR_UNPIN_BUTTON_TEST_ID
} from '@/components/home-page/jest-helpers'
import ObjectivesEmptyHero from '@/components/objectives/ObjectivesEmptyHero'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'

defineOptions({
  name: 'HomepageGroupsFilter'
})

const api = new HomepageGroupsApiHandler()

const store = useStore()
const { t } = useI18n()

const { isLoading, toggleLoading } = useLoadingState()

const homePageState = inject(homePageStateInjectionKey)
const homePageFilters = homePageState.filterValues

//#region Workspace filtering
const selectedWorkspaceIds = computed(
  () => homePageState.filterValues.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]
)

const isGroupFilteredOut = (group = {}) => {
  if (isEmpty(group)) {
    return false
  }

  const groupWorkspaceIds = group[GLOBAL_GROUP_KEYS.WORKSPACE_IDS] ?? []

  return !groupWorkspaceIds.some(id => selectedWorkspaceIds.value.includes(id))
}

const { filteredOutItemConfig } = useHomepageSidebarFilteredOutTooltip(
  'homepage.group_not_in_ws',
  () => selectedWorkspaceIds.value.length
)

//#endregion

//#region Membership mark
// the direct group memberships of the user who views Home, the same source as the default pins
const userGroupIds = computed(() => {
  const userGroups = store.state.system.userData?.userGroups ?? []

  return userGroups.map(group => group[GLOBAL_GROUP_KEYS.ID])
})

const getGroupMemberTooltip = (group = {}) => {
  return userGroupIds.value.includes(group[GLOBAL_GROUP_KEYS.ID])
    ? t('homepage.membership_mark.group')
    : null
}
//#endregion

//#region Groups navigation
const selectedGroupId = defineModel({
  required: true,
  validator: v => numberOrNullProp(v)
})

const currentGroups = ref([])
const currentParentGroup = ref(null)

const loadGroups = async groupId => {
  const { parentGroup, groups } = await api.getGroupsForFilter(groupId)

  currentGroups.value = groups
  currentParentGroup.value = parentGroup
}

const loadInitialGroups = async () => {
  toggleLoading(true)
  try {
    await loadGroups(selectedGroupId.value)
  } catch (error) {
    handleError({ error })
  } finally {
    toggleLoading(false)
  }
}

const selectGroup = groupId => {
  selectedGroupId.value = groupId

  if (homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER]) {
    homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER] = null
  }
}

const navigateToGroup = async groupId => {
  toggleLoading(true)
  try {
    await loadGroups(groupId)
    selectGroup(groupId)
  } catch (error) {
    handleError({ error })
  } finally {
    toggleLoading(false)
  }
}

const levelUp = async groupId => {
  toggleLoading(true)
  try {
    await loadGroups(groupId)
  } catch (error) {
    handleError({ error })
  } finally {
    toggleLoading(false)
  }
}

const getNestedGroups = async (parentGroup = {}) => {
  toggleLoading(true)
  try {
    const { groups } = await api.getNestedGroupsForFilter(parentGroup[GLOBAL_GROUP_KEYS.ID])

    currentGroups.value = groups
    currentParentGroup.value = cloneDeep(parentGroup)
  } catch (error) {
    handleError({ error })
  } finally {
    toggleLoading(false)
  }
}
//#endregion

//#region Search
const searchString = ref('')

const searchResults = ref(null)

const searchGroupByName = async (value = '') => {
  try {
    const { groups } = await api.getGroupsByName(value)

    // the input may have changed or been cleared while the request was in flight
    if (value !== searchString.value) {
      return
    }

    searchResults.value = groups
  } catch (error) {
    handleError({ error })
  }
}

const clearSearchResults = () => (searchResults.value = null)

watch(searchString, async newValue => {
  if (newValue) {
    await searchGroupByName(newValue)
  } else {
    clearSearchResults()
  }
})

const selectFoundedGroup = groupId => {
  searchString.value = ''
  navigateToGroup(groupId)
}
//#endregion

//#region Pinned groups
const pinnedGroupIds = computed(() => {
  return store.getters['system/homepagePinnedGroups']
})

const pinnedGroups = ref([])

const { isLoading: isPinnedGroupsLoading, toggleLoading: togglePinnedGroupsLoading } =
  useLoadingState()

const createPinnedGroupsList = async () => {
  if (isEmpty(pinnedGroupIds.value)) return

  togglePinnedGroupsLoading(true)
  try {
    const { groups } = await api.getPinnedGroupsData(pinnedGroupIds.value)

    pinnedGroups.value = groups
  } catch (error) {
    handleError({ error })
  } finally {
    togglePinnedGroupsLoading(false)
  }
}

const unpinGroup = groupId => {
  store.dispatch('system/togglePinStatusForGroup', {
    groupId
  })
}

const { isLoading: isPinsOrderUpdating, toggleLoading: togglePinsOrderUpdating } = useLoadingState()

const onPinnedGroupsReorder = async (groups = []) => {
  const snapshot = cloneDeep(pinnedGroups.value)
  const groupIds = groups.map(group => group[GLOBAL_GROUP_KEYS.ID])

  pinnedGroups.value = groups

  togglePinsOrderUpdating(true)
  try {
    const isSaved = await store.dispatch('system/updatePinnedGroupsOrder', { groupIds })

    if (!isSaved) {
      pinnedGroups.value = snapshot
    }
  } finally {
    togglePinsOrderUpdating(false)
  }
}

watch(
  pinnedGroupIds,
  async (newValue, oldValue) => {
    if (isEmpty(newValue)) {
      pinnedGroups.value = []
      return
    }

    if (!isNil(oldValue) && !isNil(newValue) && newValue.length < oldValue.length) {
      const diff = difference(oldValue, newValue)

      pinnedGroups.value = pinnedGroups.value.filter(
        group => !diff.includes(group[GLOBAL_GROUP_KEYS.ID])
      )
      return
    }

    // the same ids in a new order: the list is already reordered locally, nothing to fetch
    if (!isNil(oldValue) && isEmpty(xor(newValue, oldValue))) {
      return
    }

    await createPinnedGroupsList()
  },
  { immediate: true, deep: true }
)

//#endregion

onMounted(() => {
  loadInitialGroups()
})
</script>

<template>
  <HomepageSidebarFilterLayout v-model="searchString" :search-results="searchResults">
    <template #no-results>
      <HomepageSidebarFilterEmptyState>
        <template #hero>
          <ObjectivesEmptyHero />
        </template>
        {{ t('groups.no_groups') }}
      </HomepageSidebarFilterEmptyState>
    </template>

    <template #search-results>
      <div class="hpgf-GroupsNav_List">
        <HomepageSidebarFilterItem
          v-for="item in searchResults"
          :key="item[GLOBAL_GROUP_KEYS.ID]"
          v-tippy="{
            ...filteredOutItemConfig,
            content: isGroupFilteredOut(item) ? filteredOutItemConfig.content : null
          }"
          :filtered-out="isGroupFilteredOut(item)"
          :item="item"
          :member-tooltip="getGroupMemberTooltip(item)"
          :selected="item[GLOBAL_GROUP_KEYS.ID] === selectedGroupId"
          class="hpgf-GroupsNav_Item"
          hide-actions
          @click="selectFoundedGroup(item[GLOBAL_GROUP_KEYS.ID])"
        />
      </div>
    </template>

    <HomepageSidebarPinnedList
      :disabled="isPinsOrderUpdating || isPinnedGroupsLoading || pinnedGroups.length < 2"
      :item-key="GLOBAL_GROUP_KEYS.ID"
      :items="pinnedGroups"
      @update:items="onPinnedGroupsReorder"
    >
      <template #item="{ item }">
        <HomepageSidebarFilterItem
          v-tippy="{
            ...filteredOutItemConfig,
            content: isGroupFilteredOut(item) ? filteredOutItemConfig.content : null
          }"
          :filtered-out="isGroupFilteredOut(item)"
          :item="item"
          :member-tooltip="getGroupMemberTooltip(item)"
          :selected="item[GLOBAL_GROUP_KEYS.ID] === selectedGroupId"
          class="hpgf-GroupsNav_Item"
          pinned
          @click="navigateToGroup(item[GLOBAL_GROUP_KEYS.ID])"
        >
          <template #actions>
            <AppButtonNext
              v-tippy="{
                content: t('action.unpin'),
                placement: 'top'
              }"
              :data-auto-testid="SIDEBAR_UNPIN_BUTTON_TEST_ID"
              :data-testid="SIDEBAR_UNPIN_BUTTON_TEST_ID"
              :disabled="isPinsOrderUpdating"
              :size="BUTTON_SIZES_NEXT.XS"
              :variant="BUTTON_VARIANTS.LINK"
              class="hpgf-UnpinButton"
              icon-before="unpin"
              @click.stop="unpinGroup(item[GLOBAL_GROUP_KEYS.ID])"
            />
          </template>
        </HomepageSidebarFilterItem>
      </template>
    </HomepageSidebarPinnedList>

    <HomepageSidebarSegmentTitle>{{ t('field.groups.title') }}</HomepageSidebarSegmentTitle>

    <div
      v-if="currentParentGroup"
      :data-auto-testid="SIDEBAR_PARENT_GROUP_TEST_ID"
      :data-testid="SIDEBAR_PARENT_GROUP_TEST_ID"
      class="hpgf-Wrapper_ParentGroup"
    >
      <HomepageSidebarFilterItem
        :item="currentParentGroup"
        :member-tooltip="getGroupMemberTooltip(currentParentGroup)"
        @click="navigateToGroup(currentParentGroup[GLOBAL_GROUP_KEYS.ID])"
      >
        <template #actions>
          <AppButtonNext
            :data-auto-testid="SIDEBAR_LEVEL_UP_BUTTON_TEST_ID"
            :data-testid="SIDEBAR_LEVEL_UP_BUTTON_TEST_ID"
            :icon-density="BUTTON_ICON_DENSITIES.FIT"
            :size="BUTTON_SIZES_NEXT.XS"
            :variant="BUTTON_VARIANTS.LINK_GRAY"
            icon-before="chevron-up"
            @click.stop="levelUp(currentParentGroup[GLOBAL_GROUP_KEYS.ID])"
          />
        </template>
      </HomepageSidebarFilterItem>
    </div>

    <HomepageSidebarFilterList
      :data-auto-testid="SIDEBAR_GROUPS_LIST_TEST_ID"
      :data-testid="SIDEBAR_GROUPS_LIST_TEST_ID"
      :items="currentGroups"
      :loading="isLoading"
    >
      <template #loading>
        <HomepageGroupsFilterLoader />
      </template>

      <template #no-results>
        <HomepageSidebarFilterEmptyState>
          {{ t('workspaces.no_groups.title') }}
        </HomepageSidebarFilterEmptyState>
      </template>

      <template #item="{ item }">
        <HomepageSidebarFilterItem
          v-tippy="{
            ...filteredOutItemConfig,
            content: isGroupFilteredOut(item) ? filteredOutItemConfig.content : null
          }"
          :expandable="Boolean(item[GLOBAL_GROUP_KEYS.CHILD_COUNT])"
          :filtered-out="isGroupFilteredOut(item)"
          :item="item"
          :member-tooltip="getGroupMemberTooltip(item)"
          :selected="item[GLOBAL_GROUP_KEYS.ID] === selectedGroupId"
          class="hpgf-GroupsNav_Item"
          @click="selectGroup(item[GLOBAL_GROUP_KEYS.ID])"
          @expand="getNestedGroups(item)"
        />
      </template>
    </HomepageSidebarFilterList>
  </HomepageSidebarFilterLayout>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/homepage-mixins';

.hpgf-GroupsNav_List {
  @include homepage-sidebar-list;
}

.hpgf-GroupsNav_Item {
  @include homepage-sidebar-list-item;
}

.hpgf-Wrapper_ParentGroup {
  padding-inline: var(--ob-hps-mx);
  padding-bottom: var(--ob-hpgf-item-gap);
  margin-bottom: 14px;
  min-height: 0;
  box-shadow: 0 24px 24px -12px rgba($dark-1, 0.06);
  border-bottom: 1px solid $grey-2-next;
}

.hpgf-UnpinButton {
  --abn-color: #{$dark-2};
}
</style>
